<template>
  <page-wrapper :title="title" ref="pageEl">
    <div class="wrapper">
      <div class="row mb-3">
        <div class="col-2">
          <div class="label mb-2">ชื่อ</div>
          <el-input v-model="searchForm.name" />
        </div>
        <div class="col-2">
          <div class="label mb-2">ธนาคาร</div>
          <el-input v-model="searchForm.bankName" />
        </div>
        <div class="col-2">
          <div class="label mb-2">สถานะ</div>
          <refinance-status-select
            class="w-100"
            v-model="searchForm.status"
          ></refinance-status-select>
        </div>
        <div class="col-4">
          <div class="label mb-2">วันที่ยื่นคำขอ</div>
          <date-picker
            type="datetimerange"
            v-model="searchForm.createdAt"
          ></date-picker>
        </div>
      </div>
      <div class="text-right">
        <sc-button variant="primary" @click="handleClickSearch"
          >ค้นหา</sc-button
        >
      </div>
      <p class="mb-2 mt-2" v-if="submissions.length">{{ totalCount }}</p>
      <sc-button variant="secondary" @click="handleClickDownloadReport"
        >Download Report</sc-button
      >
    </div>
    <div class="row-header">
      <div class="cell">ลูกค้า</div>
      <div class="cell">บ้าน</div>
      <div class="cell currency">ยอดกู้</div>
      <div class="cell">ธนาคารที่ผ่อนปัจจุบัน</div>
      <div class="cell">ยื่นคำขอให้</div>
      <div class="cell">วันที่ยืนคำขอ</div>
      <div class="cell">สถานะ</div>
    </div>
    <refinance-row
      v-for="(item, index) in submissionsPresenter"
      :key="index"
      v-bind="item"
    ></refinance-row>
    <div class="wrapper text-right">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :page-size="10"
        :current-page="queryParams.page"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import Vue from "vue";
import { Input, Pagination, Select, Option } from "element-ui";
import { baanRueJaiClient } from "@/dependencies";
import { DateTime } from "luxon";

import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import DatePicker from "@/lib/components/DatePicker";
import { RefinanceStatus } from "../RefinanceStatus";
import RefinanceRow from "../components/RefinanceRow";
import RefinanceStatusSelect from "../components/RefinanceStatusSelect";

export default Vue.extend({
  components: {
    PageWrapper,
    RefinanceRow,
    ScButton,
    RefinanceStatusSelect,
    "el-input": Input,
    "el-pagination": Pagination,
    "el-select": Select,
    "el-option": Option,
    DatePicker
  },
  data() {
    const title = "รายการรีไฟเเนนซ์";
    const submissions = [];
    const total = 0;
    const searchForm = {
      name: null,
      bankName: null,
      status: null,
      createdAt: []
    };

    return { title, submissions, total, searchForm };
  },
  computed: {
    totalCount() {
      const limit = 10;
      const total = this.total.toLocaleString();
      const currentTotalItem = this.submissions.length;
      const isLastPage = limit > currentTotalItem;
      const currentPage = this.queryParams.page;
      const end = (isLastPage ? total : currentPage * limit).toLocaleString();
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString();

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`;
    },
    submissionsPresenter() {
      return this.submissions.map(submission => {
        const { form, financial_institution: financialInstitution } = submission;

        const user = {
          name: form.user.name,
          phoneNumber: form.user.phone_number
        };

        const home = {
          addressNumber: form.home.address_number,
          project: form.home.project.name
        };

        const id = submission.id;
        const loanAmount = form.loan.total;
        const bankName = form.loan.current_bank;
        const submittedToName = financialInstitution.name;
        const createdAt = submission.created_at;
        const status = RefinanceStatus[submission.status];
        const route = { name: "RefinanceDetailPage", params: { id } };

        return {
          user,
          home,
          loanAmount,
          bankName,
          submittedToName,
          createdAt,
          status,
          route
        };
      });
    },
    queryParams() {
      return {
        contactName: this.$route.query.name,
        page: parseInt(this.$route.query.page) || 1,
        bankName: this.$route.query.bankName,
        status: this.$route.query.status,
        createdAtStart: this.$route.query.createdAtStart,
        createdAtEnd: this.$route.query.createdAtEnd
      };
    }
  },
  created() {
    this.setup();
  },
  watch: {
    async "$route.query"(query) {
      await this.setupData();

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0;
    }
  },
  methods: {
    setup() {
      this.setupSearchForm();
      this.setupData();
    },
    setupSearchForm() {
      this.searchForm = {
        name: this.queryParams.contactName,
        bankName: this.queryParams.bankName,
        status: this.queryParams.status,
        createdAt:
          this.queryParams.createdAtStart && this.queryParams.createdAtEnd
            ? [
                DateTime.fromISO(this.queryParams.createdAtStart),
                DateTime.fromISO(this.queryParams.createdAtEnd)
              ]
            : []
      };
    },
    setupData() {
      this.setupSubmissions();
    },
    async setupSubmissions() {
      const {
        count,
        refinance_submissions: submissions
      } = await this.fetchSubmissions();

      this.submissions = submissions;
      this.total = count;
    },
    async fetchSubmissions() {
      const params = this.queryParams;
      const response = await baanRueJaiClient.admin.homes.refinance.listSubmissions(
        params
      );

      return response.data.result;
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page }
      };

      this.$router.replace(route);
    },
    handleClickSearch() {
      const { name, bankName, status, createdAt } = this.searchForm;
      const [createdAtStart, createdAtEnd] = createdAt;

      const route = {
        name: this.$route.name,
        query: {
          ...this.$route.query,
          createdAtStart: createdAtStart && createdAtStart.toISO(),
          createdAtEnd: createdAtEnd && createdAtEnd.toISO(),
          name,
          bankName,
          status,
          page: 1
        }
      };

      this.$router.replace(route);
    },
    async handleClickDownloadReport() {
      const response = await baanRueJaiClient.admin.homes.refinance.getReport(
        this.queryParams
      );

      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `list-refinance.xlsx`;
      link.click();
    }
  }
});
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: ($space / 2) $space;
}

.row-header {
  display: grid;
  grid-template-columns: repeat(6, 1fr) 150px;
  padding: ($space / 2) $space;

  grid-gap: 15px;

  border-bottom: $border;

  font-weight: bold;

  > .currency {
    margin-right: 30px;
    text-align: right;
  }
}
</style>



// WEBPACK FOOTER //
// src/features/refinance/pages/RefinanceListPage.vue